// ** React Imports
import { useEffect, useState } from 'react'

// ** MUI Imports
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Box from '@mui/material/Box'

// ** Third Party Imports
import {
  addDays,
  endOfDay,
  startOfDay,
  startOfYear,
  startOfMonth,
  endOfMonth,
  endOfYear,
  addMonths,
  addYears,
  startOfWeek,
  endOfWeek,
  isSameDay,
  addWeeks
} from 'date-fns'
import { DateRangePicker, RangeKeyDict } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

//** Icon Imports
import { Calendar } from 'react-feather'
import { constants } from 'src/lib/utils/constants'
import {
  CompareToFilterOptionsType,
  FilterConfigType,
  FiltersType,
  StaticRangeType,
  TabType
} from 'src/lib/types/filters'
import { IconButton, Typography } from '@mui/material'
import { Close } from 'mdi-material-ui'
import {
  getDateString,
  getDateTime,
  getEndDate,
  getSelectionStatus,
  getStartDate,
  mapStaticRangeLabel
} from 'src/lib/utils/helpers'
import { useFilter } from 'src/lib/contexts/FilterContext'
import DropdownButton from '../../form-elements/DropdownButton'
import { compareToFilterOptions, getStaticRanges } from 'src/assets/AnalyticsConfig'
import { OverviewWidgetFilterOptionsType } from 'src/lib/types/dashboardAnalytics'

type SelectionRange = {
  startDate: Date | undefined
  endDate: Date | undefined
  key: string | undefined
  compareToStartDate?: Date | undefined
  compareToEndDate?: Date | undefined
}

interface DateRangeType {
  appliedFilter?: string
  minDateValue?: Date
  maxDateValue?: Date
  parentComponent: TabType
  comparison?: boolean
}

const AdvanceDatePicker: React.FC<DateRangeType> = ({
  appliedFilter,
  minDateValue,
  maxDateValue,
  parentComponent,
  comparison = false
}) => {
  const defaultRange = {
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection'
  }
  const dateFilters: FiltersType = JSON.parse(globalThis?.window?.localStorage?.getItem(constants.FLO_FILTERS) ?? '')

  const {
    setAnalyticsFiltersCtx,
    setOrdersTableFiltersCtx,
    setAbandonedCartTableFiltersCtx,
    setAnalyticsV2FiltersCtx,
    ordersTableFilters,
    analyticsFilters,
    abandonedCartTableFilters,
    analyticsV2Filters
  } = useFilter()

  const [showDatePicker, setShowDatePicker] = useState<boolean>(false)
  const [dateRange, setDateRange] = useState<SelectionRange>(defaultRange)
  const [selectedStaticRange, setSelectedStaticRange] = useState<StaticRangeType>('today')

  const getSelectedComparisonType = () => {
    switch (parentComponent) {
      case 'abandonedCarts':
        return abandonedCartTableFilters?.comparison
      case 'analytics':
        return analyticsFilters?.comparison
      case 'orders':
        return ordersTableFilters?.comparison
      case 'analytics_v2':
        return analyticsV2Filters?.comparison
      default:
        return analyticsFilters?.comparison
    }
  }

  const setDateRangeFromContext = () => {
    switch (parentComponent) {
      case 'analytics':
        setDateRange({
          startDate: Boolean(analyticsFilters?.startDate)
            ? new Date(analyticsFilters?.startDate ?? '')
            : new Date(getStartDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)),
          endDate: Boolean(analyticsFilters?.endDate)
            ? addDays(new Date(analyticsFilters?.endDate ?? ''), -1)
            : new Date(
                addDays(
                  new Date(getEndDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)),
                  0
                )
              ),
          key: 'selection',
          ...(Boolean(analyticsFilters?.compareToStartDate)
            ? { compareToStartDate: new Date(analyticsFilters?.compareToStartDate ?? '') }
            : {}),
          ...(Boolean(analyticsFilters?.compareToEndDate)
            ? { compareToEndDate: new Date(analyticsFilters?.compareToEndDate ?? '') }
            : {})
        })
        break
      case 'orders':
        setDateRange({
          startDate: Boolean(ordersTableFilters?.startDate)
            ? new Date(ordersTableFilters?.startDate ?? '')
            : new Date(getStartDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)),
          endDate: Boolean(ordersTableFilters?.endDate)
            ? addDays(new Date(ordersTableFilters?.endDate ?? ''), -1)
            : new Date(
                addDays(
                  new Date(getEndDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)),
                  0
                )
              ),
          key: 'selection'
        })
        break
      case 'abandonedCarts':
        setDateRange({
          startDate: Boolean(abandonedCartTableFilters?.startDate)
            ? new Date(abandonedCartTableFilters?.startDate ?? '')
            : new Date(getStartDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)),
          endDate: Boolean(abandonedCartTableFilters?.endDate)
            ? addDays(new Date(abandonedCartTableFilters?.endDate ?? ''), -1)
            : new Date(
                addDays(
                  new Date(getEndDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)),
                  0
                )
              ),
          key: 'selection'
        })
        break
      case 'analytics_v2':
        setDateRange({
          startDate: Boolean(analyticsV2Filters?.startDate)
            ? new Date(analyticsV2Filters?.startDate ?? '')
            : new Date(getStartDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)),
          endDate: Boolean(analyticsV2Filters?.endDate)
            ? addDays(new Date(analyticsV2Filters?.endDate ?? ''), -1)
            : new Date(
                addDays(
                  new Date(getEndDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)),
                  0
                )
              ),
          key: 'selection'
        })
        break
    }
  }

  useEffect(() => {
    if (!Boolean(dateFilters) || !appliedFilter?.length) {
      setDateRange(defaultRange)
      return
    }
    setDateRangeFromContext()
  }, [showDatePicker])

  const handleSelect = (ranges: RangeKeyDict) => {
    const dateRangeObject = {
      startDate: ranges.selection.startDate,
      endDate: ranges.selection.endDate,
      key: ranges.selection.key
    }
    setDateRange(dateRangeObject)
  }

  const handleCompareToOptionClick = (index: number) => {
    switch (parentComponent) {
      case 'abandonedCarts':
        setAbandonedCartTableFiltersCtx({
          ...abandonedCartTableFilters,
          comparison: compareToFilterOptions[index].value as CompareToFilterOptionsType
        })
        break
      case 'analytics':
        setAnalyticsFiltersCtx({
          ...analyticsFilters,
          comparison: compareToFilterOptions[index].value as CompareToFilterOptionsType
        })
        break
      case 'orders':
        setOrdersTableFiltersCtx({
          ...ordersTableFilters,
          comparison: compareToFilterOptions[index].value as CompareToFilterOptionsType
        })
        break
      case 'analytics_v2':
        setAnalyticsV2FiltersCtx({
          ...analyticsV2Filters,
          comparison: compareToFilterOptions[index].value as CompareToFilterOptionsType
        })
        break
    }
  }

  const handleApply = () => {
    const startDate = dateRange?.startDate ? getDateString(dateRange?.startDate, 0) : ''
    const endDate = dateRange?.endDate ? getDateString(dateRange?.endDate, 1) : ''
    switch (parentComponent) {
      case 'analytics':
        setAnalyticsFiltersCtx({
          ...analyticsFilters,
          selectedStaticRange: selectedStaticRange,
          startDate: startDate,
          endDate: endDate,
          lastUpdated: `${getDateTime(new Date()).date} at ${getDateTime(new Date()).time}`
        })
        break
      case 'orders':
        setOrdersTableFiltersCtx({
          ...ordersTableFilters,
          selectedStaticRange: selectedStaticRange,
          startDate: startDate,
          endDate: endDate
        })
        break
      case 'abandonedCarts':
        setAbandonedCartTableFiltersCtx({
          ...abandonedCartTableFilters,
          selectedStaticRange: selectedStaticRange,
          startDate: startDate,
          endDate: endDate
        })
        break
      case 'analytics_v2':
        setAnalyticsV2FiltersCtx({
          ...analyticsV2Filters,
          selectedStaticRange: selectedStaticRange,
          startDate: startDate,
          endDate: endDate
        })
        break
    }

    setShowDatePicker(false)
  }

  const isSelectedDateEqualToStaticRange = () => {
    const getSelectionStatus = (selectedFilter: FilterConfigType) => {
      return (
        (!Boolean(selectedFilter?.startDate)
          ? true
          : isSameDay(
              getStartDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange),
              new Date(new Date(selectedFilter?.startDate ?? '').getTime() - 19800000)
            )) &&
        (!Boolean(selectedFilter?.endDate)
          ? true
          : isSameDay(
              getEndDate(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange),
              new Date(new Date(selectedFilter?.endDate ?? '').getTime() - 19801000)
            ))
      )
    }

    switch (parentComponent) {
      case 'analytics':
        return getSelectionStatus(analyticsFilters)
      case 'orders':
        return getSelectionStatus(ordersTableFilters)
      case 'abandonedCarts':
        return getSelectionStatus(abandonedCartTableFilters)
      case 'analytics_v2':
        return getSelectionStatus(analyticsV2Filters)
    }
  }

  return (
    <div className='flex items-center gap-2'>
      <Box
        id='date-picker-container'
        display={'flex'}
        alignItems={'center'}
        onClick={() => {
          setShowDatePicker(true)
        }}
      >
        <Button
          id='date-picker-range-button'
          variant='outlined'
          size='medium'
          color='secondary'
          className='bg-white whitespace-nowrap h-9'
          startIcon={<Calendar id='date-picker-calendar-icon' className='w-4 h-4' />}
        >
          {isSelectedDateEqualToStaticRange()
            ? mapStaticRangeLabel(dateFilters?.[parentComponent as keyof FiltersType]?.selectedStaticRange)
            : appliedFilter}
        </Button>
      </Box>

      {Boolean(comparison) ? (
        <>
          <DropdownButton
            size='large'
            title={'Compare:'}
            options={compareToFilterOptions}
            handleClick={handleCompareToOptionClick}
            variant='outlined'
            selectedOption={
              compareToFilterOptions?.findIndex(
                (option: OverviewWidgetFilterOptionsType) => option?.value === getSelectedComparisonType()
              ) === -1
                ? 0
                : compareToFilterOptions?.findIndex(
                    (option: OverviewWidgetFilterOptionsType) => option?.value === getSelectedComparisonType()
                  )
            }
          />
        </>
      ) : (
        <></>
      )}

      <Dialog
        open={showDatePicker}
        onClose={() => {
          setShowDatePicker(false)
        }}
        maxWidth='lg'
        PaperProps={{
          style: { borderRadius: 16, padding: '1rem', display: 'flex', gap: '1rem' }
        }}
      >
        <DialogTitle
          color='black'
          variant='h5'
          sx={{
            padding: '0rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          Date filters
          {/* Close Icon */}
          <IconButton
            aria-label='close'
            onClick={() => {
              setShowDatePicker(false)
            }}
          >
            <Close color={'secondary'} sx={{ transform: 'scale(1.3)' }} />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: '0rem' }}>
          <DateRangePicker
            ranges={[dateRange]}
            onChange={handleSelect}
            months={2}
            editableDateInputs
            minDate={minDateValue ?? undefined}
            maxDate={maxDateValue ?? undefined}
            direction={'horizontal'}
            staticRanges={getStaticRanges(setSelectedStaticRange)}
            inputRanges={[]}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '0rem' }}>
          <Button
            variant='text'
            color={'secondary'}
            onClick={() => {
              setShowDatePicker(false)
            }}
          >
            Cancel
          </Button>
          <Button variant={'contained'} onClick={handleApply}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default AdvanceDatePicker
